
/*
  TIMI DESIGNS SEARCH v1.5
  October 15 Draft Theme

  Features:
  - Popular designs ranked by actual sales
  - All designs
  - Smart search across the full design library
  - Automatic category tabs from Zepto tags
  - Friendly design names
  - Native Zepto hover tooltip
  - Restored original artwork thumbnails
  - Friendly selected-design label

  Preserves:
  - Original Zepto design IDs
  - Original artwork URLs
  - Selection handlers
  - Pricing
  - Live preview
  - Cart personalization data
*/

(() => {
  'use strict';

  if (window.__timiDesignSearchV15) return;
  window.__timiDesignSearchV15 = true;

  const FIELD =
    '.product-personalizer .pplr-wrapper.pplr-timi-designs';

  const CHOICE =
    '.pplr-swatch-element.pplrimage[data-val]';

  /*
    POPULAR DESIGNS

    Ordered according to TIMI sales data.

    Matching is based on original Zepto IDs.
    No backend IDs are changed.
  */

  const POPULAR = [
    /i.brought.my.knitting.just.in.case/,
    /i.brought.my.crochet.just.in.case/,
    /i.brought.my.yarn.just.in.case/,
    /varsity.knitter.*(3d|puff)|(?:3d|puff).*varsity.knitter/,
    /sweater.weather.*(3d|puff)|(?:3d|puff).*sweater.weather/,
    /anti.social.knitting.club/,
    /hobby.ho(?:e|\b)/,
    /knitter.*chain|chain.*knitter/,
    /knit.sox.*(3d|puff)|(?:3d|puff).*knit.sox/
  ];

  /*
    FRIENDLY NAME OVERRIDES

    Add specific design names here whenever
    the automatic formatting isn't ideal.
  */

  const NAME_OVERRIDES = {
    'Anti-Social-Knitting-Club-TIMI':
      'Anti Social Knitting Club',

    'Hobby-Ho-chain-TIMI':
      'Hobby Hoe',

    'Fiber-fest-enthusiast-TIMI':
      'Fiber Fest Enthusiast'
  };

  const states = new WeakMap();

  /*
    TEXT HELPERS
  */

  function normal(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function original(el) {
    return el.getAttribute('data-val') || '';
  }

  function friendlyName(id) {
    if (!id) return '';

    if (NAME_OVERRIDES[id]) {
      return NAME_OVERRIDES[id];
    }

    return id
      .replace(/(?:[-_ ]TIMI)+$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, letter =>
        letter.toUpperCase()
      )
      .replace(/\b3d\b/gi, '3D')
      .replace(/\bJust In Case\b/gi, 'Just in Case')
      .replace(/\bChain Stitch\b/gi, 'Chainstitch')
      .trim();
  }

  function setText(element, text) {
    if (
      element &&
      element.textContent !== text
    ) {
      element.textContent = text;
    }
  }

  function getChoices(field) {
    return [
      ...field.querySelectorAll(CHOICE)
    ];
  }

  /*
    FRIENDLY NAMES

    Uses Zepto's existing tooltip,
    just like timi-icon-names.js.

    No extra hover labels.
  */

  function updateFriendlyNames(field) {
    getChoices(field).forEach(swatch => {
      const id = original(swatch);
      if (!id) return;

      const name = friendlyName(id);

      swatch.querySelectorAll(
        '.swtooltip > span, ' +
        '.img_dropdown .pplr-selecter-selected-label'
      ).forEach(label => {
        setText(label, name);
      });

      if (
        swatch.getAttribute('aria-label') !== name
      ) {
        swatch.setAttribute(
          'aria-label',
          name
        );
      }

      // Avoid a duplicate browser tooltip.
      swatch.removeAttribute('title');

      // Remove attributes from older versions.
      swatch.removeAttribute(
        'data-timi-friendly-name'
      );
    });

    /*
      Update selected name beside
      the TIMI Designs heading.
    */

    const selectedLabel = field.querySelector(
      '.pplr_option_text_span.f_p_img'
    );

    const selectedSwatch = field.querySelector(
      '.pplr-swatch-element.pplrimage.selected[data-val]'
    );

    if (
      selectedLabel &&
      selectedSwatch
    ) {
      setText(
        selectedLabel,
        ' - ' + friendlyName(
          original(selectedSwatch)
        )
      );
    }
  }

  /*
    POPULAR RANKING
  */

  function popularRank(el) {
    const id = normal(original(el))
      .replace(/ /g, '.');

    return POPULAR.findIndex(pattern =>
      pattern.test(id)
    );
  }

  /*
    CATEGORIES

    Reads the existing data-tag values.

    Popular and All are generated here;
    neither needs a Zepto tag.
  */

  function tagsFor(el) {
    return String(
      el.getAttribute('data-tag') || ''
    )
      .split(/[,;|]/)
      .map(tag => tag.trim())
      .filter(Boolean);
  }

  function categories(field) {
    const unique = new Map();

    getChoices(field)
      .flatMap(tagsFor)
      .forEach(tag => {
        if (
          /^(all|popular|most popular)$/i.test(tag)
        ) {
          return;
        }

        const key = normal(tag);

        if (
          key &&
          !unique.has(key)
        ) {
          unique.set(key, tag);
        }
      });

    return [...unique.values()]
      .sort((a, b) =>
        a.localeCompare(b)
      );
  }

  /*
    SMART SEARCH KEYWORDS
  */

  function searchable(el) {
    const id = normal(original(el));

    const name = normal(
      friendlyName(original(el))
    );

    const tags = normal(
      el.getAttribute('data-tag')
    );

    const extras = [];

    /*
      Rhinebeck and fiber festivals.
    */

    if (
      /rhinebeck|fiber fest|fiber festival|sheep and wool/.test(id)
    ) {
      extras.push(
        'rhinebeck fiber fest festival sheep ' +
        'wool yarn event show'
      );
    }

    /*
      Related festival / project designs.
    */

    if (
      /i brought my (knitting|crochet|yarn)/.test(id)
    ) {
      extras.push(
        'rhinebeck fiber fest festival ' +
        'project travel just in case'
      );
    }

    /*
      Embroidery techniques.
    */

    if (/3d|puff/.test(id)) {
      extras.push(
        '3d puff raised dimensional'
      );
    }

    if (/chain.?stitch|chain/.test(id)) {
      extras.push(
        'chainstitch chain stitch'
      );
    }

    /*
      Fiber arts.
    */

    if (/knit|yarn/.test(id)) {
      extras.push(
        'knitting knitter yarn fiber craft'
      );
    }

    if (/crochet/.test(id)) {
      extras.push(
        'crocheting crocheter yarn fiber craft'
      );
    }

    /*
      Seasonal.
    */

    if (/sweater|autumn|fall/.test(id)) {
      extras.push(
        'cozy fall autumn winter sweater'
      );
    }

    /*
      Funny designs.
    */

    if (/anti social|hobby ho/.test(id)) {
      extras.push(
        'funny snarky humor'
      );
    }

    /*
      Sports.
    */

    if (/knit sox/.test(id)) {
      extras.push(
        'baseball sports knit'
      );
    }

    return [
      id,
      name,
      tags,
      ...extras
    ].join(' ');
  }

  function matches(el, search) {
    const query = normal(search);

    if (!query) return true;

    const synonyms = {
      '3d puff': '3d puff',
      'puff': 'puff',
      'raised': 'puff',
      'dimensional': 'puff',

      'chain stitch': 'chainstitch',
      'chainstitch': 'chainstitch',

      'fiber festival': 'fiber fest',
      'sheep and wool': 'rhinebeck',

      'crocheter': 'crochet',
      'crocheting': 'crochet',

      'knitter': 'knit',
      'knitting': 'knit'
    };

    const term =
      synonyms[query] || query;

    const haystack = searchable(el);

    return term.split(' ').every(word =>
      haystack.includes(word)
    );
  }

  /*
    ARTWORK RESTORATION

    Uses the original Zepto image URL.

    This is the same approach used in
    the working TIMI icon search.
  */

  function restoreArtwork(el) {
    const image =
      el.getAttribute('data-image');

    if (
      !image ||
      !/^https:\/\//i.test(image)
    ) {
      return;
    }

    const artwork =
      `url("${image.replace(/"/g, '%22')}")`;

    if (
      el.style.getPropertyValue(
        'background-image'
      ) !== artwork ||
      el.style.getPropertyPriority(
        'background-image'
      ) !== 'important'
    ) {
      el.style.setProperty(
        'background-image',
        artwork,
        'important'
      );
    }
  }

  /*
    VISIBILITY

    Hide nonmatching choices.

    Don't force display:block on
    visible choices.
  */

  function setVisible(el, visible) {
    if (visible) {
      if (
        el.style.getPropertyValue(
          'display'
        ) === 'none' &&
        el.style.getPropertyPriority(
          'display'
        ) === 'important'
      ) {
        el.style.removeProperty(
          'display'
        );
      }
    } else {
      if (
        el.style.getPropertyValue(
          'display'
        ) !== 'none' ||
        el.style.getPropertyPriority(
          'display'
        ) !== 'important'
      ) {
        el.style.setProperty(
          'display',
          'none',
          'important'
        );
      }
    }
  }

  /*
    APPLY POPULAR ORDER

    CSS ordering only.

    No Zepto elements are removed,
    cloned, or moved in the DOM.
  */

  function updateOrder(choices, searching) {
    choices.forEach((el, index) => {
      const rank = popularRank(el);

      const order = searching
        ? index
        : rank >= 0
          ? rank
          : 1000 + index;

      const value = String(order);

      if (
        el.style.getPropertyValue(
          'order'
        ) !== value
      ) {
        el.style.setProperty(
          'order',
          value
        );
      }
    });
  }

  /*
    UPDATE RESULTS
  */

  function update(state) {
    if (!state.toolbar.isConnected) {
      return;
    }

    const choices =
      getChoices(state.field);

    const query =
      state.search.value.trim();

    const searching =
      query.length > 0;

    const mode =
      state.mode;

    /*
      A search checks every TIMI Design,
      regardless of the active tab.
    */

    const matching = choices.filter(el => {
      if (searching) {
        return matches(el, query);
      }

      if (mode === 'All') {
        return true;
      }

      if (mode === 'Popular') {
        return popularRank(el) >= 0;
      }

      return tagsFor(el).some(tag =>
        normal(tag) === normal(mode)
      );
    });

    const visible = new Set(matching);

    choices.forEach(el => {
      const show = visible.has(el);

      setVisible(el, show);

      if (show) {
        restoreArtwork(el);
      }
    });

    /*
      Sort Popular by sales rank.

      Other modes keep their original
      Zepto order.
    */

    updateOrder(
      choices,
      searching || mode !== 'Popular'
    );

    updateFriendlyNames(state.field);

    /*
      Count.
    */

    state.count.textContent =
      `${matching.length} design${
        matching.length === 1 ? '' : 's'
      }`;

    /*
      During search, no tab appears
      selected, since all designs
      are being searched.
    */

    state.buttons.forEach(button => {
      const active =
        !searching &&
        button.dataset.category === mode;

      button.classList.toggle(
        'is-active',
        active
      );

      button.setAttribute(
        'aria-pressed',
        String(active)
      );
    });

    state.toolbar.classList.toggle(
      'timi-is-searching',
      searching
    );
  }

  /*
    POPULAR / ALL / CATEGORY TABS
  */

  function buildTabs(state) {
    const items = [
      'Popular',
      'All',
      ...categories(state.field)
    ];

    state.tabs.replaceChildren();

    state.buttons = items.map(category => {
      const button =
        document.createElement('button');

      button.type = 'button';

      button.className =
        'timi-design-search-tab';

      button.dataset.category =
        category;

      button.textContent =
        category;

      state.tabs.appendChild(button);

      return button;
    });

    if (!items.includes(state.mode)) {
      state.mode = 'All';
    }
  }

  /*
    INITIALIZE
  */

  function setup(field) {
    if (!getChoices(field).length) {
      return;
    }

    const existing = states.get(field);

    if (
      existing &&
      existing.toolbar.isConnected
    ) {
      const currentTags =
        categories(field).join('|');

      if (
        currentTags !== existing.tags
      ) {
        existing.tags = currentTags;
        buildTabs(existing);
      }

      update(existing);
      return;
    }

    const toolbar =
      document.createElement('div');

    toolbar.className =
      'timi-design-search';

    toolbar.innerHTML = `
      <label class="timi-design-search-label">
        Find a TIMI Design
      </label>

      <input
        type="search"
        class="timi-design-search-input"
        placeholder="Search knitting, Rhinebeck, 3D puff, chainstitch..."
        aria-label="Search TIMI Designs"
        autocomplete="off"
      >

      <div
        class="timi-design-search-tabs"
        role="group"
        aria-label="Browse TIMI Designs"
      ></div>

      <p
        class="timi-design-search-count"
        aria-live="polite"
      ></p>
    `;

    const label = field.querySelector(
      ':scope > .pplrlabel'
    );

    if (label) {
      label.after(toolbar);
    } else {
      field.prepend(toolbar);
    }

    const state = {
      field,
      toolbar,

      search: toolbar.querySelector(
        '.timi-design-search-input'
      ),

      tabs: toolbar.querySelector(
        '.timi-design-search-tabs'
      ),

      count: toolbar.querySelector(
        '.timi-design-search-count'
      ),

      buttons: [],
      tags: '',
      mode: 'Popular'
    };

    states.set(field, state);

    /*
      Search input.
    */

    state.search.addEventListener(
      'input',
      () => update(state)
    );

    /*
      Category button clicks.
    */

    state.tabs.addEventListener(
      'click',
      event => {
        const button =
          event.target.closest(
            '.timi-design-search-tab'
          );

        if (!button) return;

        state.mode =
          button.dataset.category;

        state.search.value = '';

        update(state);
      }
    );

    state.tags =
      categories(field).join('|');

    buildTabs(state);
    update(state);
  }

  /*
    STYLING

    Zepto's original hover tooltip
    is used for friendly names.

    No additional custom tooltip.
  */

  function injectCSS() {
    if (
      document.getElementById(
        'timi-design-search-styles'
      )
    ) return;

    const style =
      document.createElement('style');

    style.id =
      'timi-design-search-styles';

    style.textContent = `
      .timi-design-search {
        margin: 7px 0 12px;
        grid-column: 1 / -1;
      }

      .timi-design-search-label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 6px;
      }

      .timi-design-search-input {
        box-sizing: border-box;
        width: 100%;
        min-height: 40px;
        padding: 9px 12px;
        border: 1px solid #ded1d4;
        border-radius: 9px;
        background: #fff;
        color: #28252a;
        font-size: 12px;
      }

      .timi-design-search-input:focus-visible {
        outline: 2px solid #3e5e92;
        outline-offset: 2px;
      }

      .timi-design-search-tabs {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin: 9px 0 5px;
      }

      .timi-design-search-tab {
        appearance: none;
        border: 1px solid #e9d9d9;
        border-radius: 18px;
        background: #fbf5ea;
        color: #8f182f;
        padding: 7px 11px;
        cursor: pointer;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.2;
      }

      .timi-design-search-tab.is-active {
        background: #8f182f;
        color: #fff;
        border-color: #8f182f;
      }

      .timi-design-search-tab:hover {
        border-color: #8f182f;
      }

      .timi-design-search-tab:focus-visible {
        outline: 2px solid #3e5e92;
        outline-offset: 2px;
      }

      .timi-design-search-count {
        font-size: 11px;
        color: #70676b;
        margin: 4px 0 0;
      }

      /*
        Preserve the original Zepto
        thumbnail dimensions and artwork.

        CSS order only affects layout in
        a flex or grid container.
      */
    `;

    document.head.appendChild(style);
  }

  /*
    STARTUP + ZEPTO REBUILD SUPPORT
  */

  function init() {
    document
      .querySelectorAll(FIELD)
      .forEach(setup);
  }

  function start() {
    injectCSS();
    init();

    let scheduled = false;

    function scheduleUpdate() {
      if (scheduled) return;

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        init();
      });
    }

    const observer = new MutationObserver(
      records => {
        const relevant = records.some(record => {
          const target = record.target;

          if (
            target.nodeType === 1 &&
            (
              target.matches?.(FIELD) ||
              target.closest?.(FIELD)
            )
          ) {
            return true;
          }

          if (record.type === 'childList') {
            return [...record.addedNodes].some(
              node =>
                node.nodeType === 1 &&
                (
                  node.matches?.(FIELD) ||
                  node.querySelector?.(FIELD)
                )
            );
          }

          return false;
        });

        if (relevant) {
          scheduleUpdate();
        }
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    document.addEventListener(
      'click',
      event => {
        if (
          event.target.closest?.(
            FIELD + ' ' + CHOICE
          )
        ) {
          setTimeout(init, 100);
        }
      },
      true
    );

    window.addEventListener(
      'pageshow',
      init
    );

    setTimeout(init, 500);
    setTimeout(init, 1500);

    console.log(
      'TIMI Design Search v1.5 initialized'
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
